// Welche Seite gilt, entscheidet der Status des Kontos.
//
// Es gibt genau drei Zustaende: nicht angemeldet, angemeldet und wartend,
// freigeschaltet. Diese Reihenfolge ist auch die der Pruefung, damit ein
// Wartekonto nie versehentlich eine Datenseite sieht (Projektplan 11.2).

import { Navigate, Route, Routes } from "react-router-dom";
import { useMe } from "./lib/me.js";
import { Anmelden } from "./seiten/Anmelden.js";
import { Warten } from "./seiten/Warten.js";
import { Hauptscreen } from "./seiten/Hauptscreen.js";
import { Einstellungen } from "./seiten/Einstellungen.js";
import { Statistik } from "./seiten/Statistik.js";
import { StatistikDetail } from "./seiten/StatistikDetail.js";
import { Leaderboard } from "./seiten/Leaderboard.js";
import { LetzteKennzeichen } from "./seiten/LetzteKennzeichen.js";
import { KartenSeite } from "./seiten/Karte.js";
import { Verwaltung } from "./seiten/Verwaltung.js";
import { Einladung } from "./seiten/Einladung.js";
import { SammlungEinladung } from "./seiten/SammlungEinladung.js";
import { Import } from "./seiten/Import.js";
import { Backup } from "./seiten/Backup.js";
import { Schnell } from "./seiten/Schnell.js";
import { Karte, Rahmen } from "./bausteine/Bausteine.js";
import { IosInstallHinweis } from "./bausteine/IosInstallHinweis.js";
import { OfflineBanner } from "./bausteine/OfflineBanner.js";

export function App() {
  const { data, isPending, error } = useMe();

  if (isPending) {
    return (
      <Rahmen
        kinder={
          <Karte
            kinder={<p className="text-sm text-neutral-500">Einen Moment...</p>}
          />
        }
      />
    );
  }

  if (error) {
    return (
      <Rahmen
        kinder={
          <Karte
            kinder={
              <p className="text-sm text-red-700">
                Die API ist nicht erreichbar. Bitte später noch einmal
                versuchen.
              </p>
            }
          />
        }
      />
    );
  }

  // Der Einladungslink muss auch ohne Anmeldung erreichbar sein, sonst laesst
  // er sich nicht einloesen.
  const einladungsRoute = (
    <Route path="/einladung/:token" element={<Einladung />} />
  );

  if (!data?.angemeldet || !data.nutzer) {
    return (
      <Routes>
        {einladungsRoute}
        <Route path="*" element={<Anmelden />} />
      </Routes>
    );
  }

  const nutzer = data.nutzer;

  if (nutzer.status !== "active") {
    return (
      <Routes>
        {einladungsRoute}
        <Route path="*" element={<Warten nutzer={nutzer} />} />
      </Routes>
    );
  }

  const sammlungen = data.sammlungen ?? [];

  return (
    <>
      <IosInstallHinweis />
      <OfflineBanner />
      <Routes>
        {einladungsRoute}
        <Route
          path="/"
          element={<Hauptscreen nutzer={nutzer} sammlungen={sammlungen} />}
        />
        <Route
          path="/einstellungen"
          element={<Einstellungen nutzer={nutzer} sammlungen={sammlungen} />}
        />
        <Route
          path="/statistik"
          element={<Statistik nutzer={nutzer} sammlungen={sammlungen} />}
        />
        <Route
          path="/statistik/:bereich"
          element={<StatistikDetail nutzer={nutzer} sammlungen={sammlungen} />}
        />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route
          path="/letzte-kennzeichen"
          element={
            <LetzteKennzeichen nutzer={nutzer} sammlungen={sammlungen} />
          }
        />
        <Route
          path="/karte"
          element={<KartenSeite nutzer={nutzer} sammlungen={sammlungen} />}
        />
        {/* Anders als /einladung/:token (Konto-Einladung) nur hier erreichbar:
            Beitreten braucht ein freigeschaltetes Konto. Ein nicht
            angemeldetes oder wartendes Konto sieht stattdessen die normale
            Anmelde-/Wartenseite und kann den Link (14 Tage gueltig) danach
            erneut oeffnen. */}
        <Route
          path="/sammlung-einladung/:token"
          element={<SammlungEinladung />}
        />
        <Route
          path="/schnell"
          element={<Schnell nutzer={nutzer} sammlungen={sammlungen} />}
        />
        <Route
          path="/import"
          element={<Import nutzer={nutzer} sammlungen={sammlungen} />}
        />
        <Route path="/backup" element={<Backup sammlungen={sammlungen} />} />
        <Route
          path="/verwaltung"
          element={
            nutzer.role === "admin" ? (
              <Verwaltung />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
